import {
  ChangeEvent,
  ChangeEventHandler,
  KeyboardEventHandler,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@material-ui/core";
import ButtonWithLoading from "components/ButtonWithLoading";
import apiRequest from "utils/apiRequestWithErrorCode";
import { useStyles } from "../styled";

import {
  ActionType,
  AuthenticationMethod,
  DeliveryManagerSettings,
  VisibilityScope,
} from "@dashboard-v3/api";

interface Props {
  settings: DeliveryManagerSettings;
}

// Maximum hours a user can enter manually. For longer-lived sessions the user
// must instead enable the "no expiration" option. Session lifetime is enforced
// as a browser cookie maxAge in /app.
const MAX_SESSION_DURATION_HOURS = 48;
// Fixed sentinel used by the "no expiration" option. It is above
// MAX_SESSION_DURATION_HOURS on purpose, so it can never be reached by manual
// input and can be reliably detected when loading an existing configuration.
const NO_EXPIRATION_HOURS = 10_000;

type SettingsForm = {
  /** Session duration in hours */
  sessionDuration: number;
  noExpiration: boolean;
  methods: AuthenticationMethod[];
  requestApprovalVisibility: VisibilityScope;
  sendDelayVisibility: VisibilityScope;
};

export default function EditConfiguration({ settings }: Props) {
  const { t } = useTranslation("storage");
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<SettingsForm>(initForm(settings));
  const [sessionDurationError, setSessionDurationError] = useState("");
  const classes = useStyles();

  const onLoginOptionsChange: ChangeEventHandler<HTMLInputElement> = e => {
    const { name, checked } = e.target;

    setForm(prev => {
      const update = { ...prev };
      if (checked) {
        update.methods = [...prev.methods, name as AuthenticationMethod];
      } else {
        update.methods = update.methods.filter(m => m !== name);
      }

      return update;
    });
  };

  const onSessionDurationChange: ChangeEventHandler<HTMLInputElement> = e => {
    const { value } = e.target;
    setSessionDurationError("");

    setForm(prev => {
      const update = { ...prev };
      update.sessionDuration = parseInt(value, 10);

      return update;
    });
  };

  const onNoExpirationChange: ChangeEventHandler<HTMLInputElement> = e => {
    const { checked } = e.target;
    setSessionDurationError("");

    setForm(prev => ({ ...prev, noExpiration: checked }));
  };

  const onVisibilityScopeChange =
    (action: ActionType) => (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      setForm(prev => {
        const visibilityScope = value as VisibilityScope;

        if (action === "REQUEST_APPROVAL") {
          return { ...prev, requestApprovalVisibility: visibilityScope };
        }

        if (action === "SEND_DELAY") {
          return { ...prev, sendDelayVisibility: visibilityScope };
        }
      });
    };

  const onSessionDurationKeyDown: KeyboardEventHandler<
    HTMLInputElement
  > = e => {
    const disallowedChars = [".", ",", "e", "E", "+", "-"];

    if (disallowedChars.includes(e.key)) e.preventDefault();
  };

  const sessionDurationBlur = () => {
    if (form.noExpiration) return;

    setSessionDurationError(() => {
      if (form.sessionDuration < 1 || isNaN(form.sessionDuration)) {
        return t("deliveryManager.form.sessionDurationError");
      }
      if (form.sessionDuration > MAX_SESSION_DURATION_HOURS) {
        return t("deliveryManager.form.sessionDurationMaxError", {
          hourLimit: MAX_SESSION_DURATION_HOURS,
        });
      }
      return "";
    });
  };

  const onSubmit = async () => {
    setLoading(true);

    try {
      await apiRequest<DeliveryManagerSettings>(
        "PATCH",
        "/delivery-manager/settings",
        {
          loginOptions: form.methods,
          sessionDuration: hoursToSeconds(
            form.noExpiration ? NO_EXPIRATION_HOURS : form.sessionDuration
          ),
          permissions: {
            sendDelayVisibility: form.sendDelayVisibility,
            requestApprovalVisibility: form.requestApprovalVisibility,
          },
        }
      );
      enqueueSnackbar(t("deliveryManager.form.saveSuccess"), {
        variant: "success",
      });
    } catch (e) {
      enqueueSnackbar(t("deliveryManager.form.createError"), {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  function disableSubmit() {
    if (!form.methods.length) return true;
    if (form.noExpiration) return false;

    return (
      !form.sessionDuration ||
      form.sessionDuration < 1 ||
      form.sessionDuration > MAX_SESSION_DURATION_HOURS
    );
  }

  return (
    <>
      <Box className={classes.title}>
        <Typography variant="subtitle2">
          {t("deliveryManager.form.authTitle")}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {t("deliveryManager.form.authSubtitle")}
        </Typography>
      </Box>
      <div className={classes.optionContainer}>
        {getAuthOptions(form.methods).map(option => (
          <FormControlLabel
            key={`auth_option_${option.name}`}
            name={option.name}
            label={
              <Typography className={classes.optionLabel}>
                {t(option.label)}
              </Typography>
            }
            control={
              <Checkbox
                size="small"
                color="primary"
                checked={option.checked}
                onChange={onLoginOptionsChange}
              />
            }
          />
        ))}
      </div>
      <Divider style={{ marginTop: 12 }} />
      <Box className={classes.title}>
        <Typography variant="subtitle2">
          {t("deliveryManager.form.sessionDurationTitle")}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {t("deliveryManager.form.sessionDurationSubtitle", {
            hourLimit: MAX_SESSION_DURATION_HOURS,
            daysLimit: MAX_SESSION_DURATION_HOURS / 24,
          })}
        </Typography>
      </Box>
      <Box
        className={classes.optionContainer}
        display="flex"
        alignItems="center"
        gridGap="1rem"
        style={{ marginTop: "1rem" }}
      >
        <TextField
          name="sessionDuration"
          type="number"
          label={t("deliveryManager.form.sessionDurationLabel")}
          inputProps={{ min: 1, step: 1, max: MAX_SESSION_DURATION_HOURS }}
          style={{ width: "20%" }}
          variant="outlined"
          disabled={form.noExpiration}
          value={form.noExpiration ? "" : form.sessionDuration}
          onChange={onSessionDurationChange}
          onKeyDown={onSessionDurationKeyDown}
          onBlur={sessionDurationBlur}
          error={Boolean(sessionDurationError)}
          helperText={Boolean(sessionDurationError) && sessionDurationError}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              color="primary"
              checked={form.noExpiration}
              onChange={onNoExpirationChange}
            />
          }
          label={
            <Typography className={classes.optionLabel}>
              {t("deliveryManager.form.sessionDurationNoExpirationLabel")}
            </Typography>
          }
        />
      </Box>
      <Divider style={{ marginTop: 12 }} />
      <Box className={classes.title}>
        <Typography variant="subtitle2">
          {t("deliveryManager.form.temporaryHoldPermissionsTitle")}
        </Typography>
      </Box>
      <Box className={classes.visibilityGroup}>
        <Typography variant="body2" className={classes.subLabel}>
          {t("deliveryManager.form.messageVisibilityLabel")}
        </Typography>
        <div className={classes.visibilityOptions}>
          <RadioGroup
            value={form.sendDelayVisibility}
            onChange={onVisibilityScopeChange("SEND_DELAY")}
          >
            <FormControlLabel
              value="TEAM"
              classes={{ root: classes.radioRoot }}
              control={<Radio color="primary" size="small" />}
              label={
                <Box>
                  <Typography className={classes.optionLabel}>
                    {t("deliveryManager.form.visibilityScopeTeamLabel")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t("deliveryManager.form.temporaryHoldScopeTeamDesc")}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="OWN"
              classes={{ root: classes.radioRoot }}
              control={<Radio color="primary" size="small" />}
              label={
                <Box>
                  <Typography className={classes.optionLabel}>
                    {t("deliveryManager.form.visibilityScopeOwnLabel")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t("deliveryManager.form.temporaryHoldScopeOwnDesc")}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="ACTED_ON"
              classes={{ root: classes.radioRoot }}
              control={<Radio color="primary" size="small" />}
              label={
                <Box>
                  <Typography className={classes.optionLabel}>
                    {t("deliveryManager.form.visibilityScopeActedOnLabel")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t("deliveryManager.form.temporaryHoldScopeActedOnDesc")}
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </div>
      </Box>
      <Divider style={{ marginTop: 12 }} />
      <Box className={classes.title}>
        <Typography variant="subtitle2">
          {t("deliveryManager.form.requestApprovalPermissionsTitle")}
        </Typography>
      </Box>
      <Box className={classes.visibilityGroup}>
        <Typography variant="body2" className={classes.subLabel}>
          {t("deliveryManager.form.messageVisibilityLabel")}
        </Typography>
        <div className={classes.visibilityOptions}>
          <RadioGroup
            value={form.requestApprovalVisibility}
            onChange={onVisibilityScopeChange("REQUEST_APPROVAL")}
          >
            <FormControlLabel
              value="TEAM"
              classes={{ root: classes.radioRoot }}
              control={<Radio color="primary" size="small" />}
              label={
                <Box>
                  <Typography className={classes.optionLabel}>
                    {t("deliveryManager.form.visibilityScopeTeamLabel")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t("deliveryManager.form.requestApprovalScopeTeamDesc")}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="OWN"
              classes={{ root: classes.radioRoot }}
              control={<Radio color="primary" size="small" />}
              label={
                <Box>
                  <Typography className={classes.optionLabel}>
                    {t("deliveryManager.form.visibilityScopeOwnLabel")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t("deliveryManager.form.requestApprovalScopeOwnDesc")}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="ACTED_ON"
              classes={{ root: classes.radioRoot }}
              control={<Radio color="primary" size="small" />}
              label={
                <Box>
                  <Typography className={classes.optionLabel}>
                    {t("deliveryManager.form.visibilityScopeActedOnLabel")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t("deliveryManager.form.requestApprovalScopeActedOnDesc")}
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </div>
      </Box>

      <Box mt={2} display="flex" flexDirection="column" gridGap="1rem">
        <ButtonWithLoading
          size="small"
          variant="contained"
          disableElevation
          loading={loading}
          onClick={onSubmit}
          disabled={disableSubmit()}
        >
          {t("deliveryManager.form.submitBtn")}
        </ButtonWithLoading>
      </Box>
    </>
  );
}

const getAuthOptions = (mehtods: AuthenticationMethod[]) => {
  const methods: AuthenticationMethod[] = ["CODE", "GOOGLE", "MICROSOFT"];

  return methods.map(method => {
    return {
      name: method,
      label: `mxheroShares.auth.method.${method}`,
      checked: mehtods.includes(method),
    };
  });
};

function initForm(settings: DeliveryManagerSettings) {
  const storedHours = secondsToHours(settings?.sessionDuration);
  const noExpiration = storedHours === NO_EXPIRATION_HOURS;

  return () => ({
    // When "no expiration" is stored, keep a sane default in the (disabled)
    // input so unchecking the option yields a usable value.
    sessionDuration: noExpiration ? 1 : storedHours || 1,
    noExpiration,
    methods: settings?.loginOptions || [],
    requestApprovalVisibility:
      settings?.permissions?.requestApprovalVisibility ||
      ("TEAM" as VisibilityScope),
    sendDelayVisibility:
      settings?.permissions?.sendDelayVisibility || ("TEAM" as VisibilityScope),
  });
}

function hoursToSeconds(value: number) {
  return 60 * 60 * value;
}

function secondsToHours(value: number, defaultValue?: number) {
  if (typeof value === "undefined" || value === null) return defaultValue;
  return value / 60 / 60;
}
